import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ViewContainerRef, ComponentRef, AfterViewInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Observable, Subject } from 'rxjs';
import { first, share, shareReplay, take, takeUntil } from 'rxjs/operators';


import { AssessmentFormService, SettingsFlagsResult } from '../../services/assessment-form.service';
import { InitialAssessmentForm } from '../../data/initial-assessment-form';
import { IPageDetails } from '../../data/page-details';
import { IFormTypes } from '../../models/form-types';
import { NavigationService } from '../../services/navigation.service';
import { FileService } from '../../services/file.service';
import { NotificationService } from '../../services/notification.service';
import { SponsorDetails } from '../../data/sponsor-details';
import { SaveFormDialogComponent } from '../../components/save-form-dialog/save-form-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DeleteFormDialogComponent } from '../../components/delete-form-dialog/delete-form-dialog.component';
import { FormGroup } from '@angular/forms';
import { IFormComponent } from '../../models/i-form-component';
import { ComponentFactoryService } from '../../services/component-factory.service';
import { ComponentFactoryInput } from '../../models/component-factory-input';
import { LookupDataService } from '../../services/lookup-data.service';
import { ComponentFactoryData } from '../../models/component-factory-data';
import { SubmitFormDialogComponent } from '../../components/submit-form-dialog/submit-form-dialog.component';

enum SettingsFlags {
  isAddressForced = 1
}


@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss']
})
export class MainComponent implements OnInit, OnDestroy, AfterViewInit {
  





  childForm: FormGroup<any> | undefined; 
  
  onChildFormReady($event: FormGroup<any>) {
    console.log(`ChildReady called by ${this.getCurrentPage()}`);
    this.childForm = $event;
  }

  private destroy$ = new Subject<void>();
  componentLoading = false;
  currentComponent: IFormComponent | null = null;
  viewInitialized = false; 
  lookupDataLoaded = false;
  pendingPageToLoad?: string;

  @ViewChild('assessmentForm') assessmentForm!: ElementRef;
  @ViewChild('dynamicComponent', { read: ViewContainerRef }) dynamicComponent!: ViewContainerRef;
    
  private lookupData: any = {};

  // Form state
  businessId: number = 0;
  formSubmitted = false;
  model!: InitialAssessmentForm;
  fileInProgress = false;
  fileUploadErrorMessage: string | null = null;
  enquiryType = 'visa';
  flags: number = 0;
  settingsFlags: number = 0;
  showDeleteDialog = false;
  wasLoaded = false;

  saveResultMessage: any;
  saveResultMessageType: any;

  selectedForm: IPageDetails | null = null;
  formTypes!: IFormTypes;
  fadeOutAlert: boolean = false;
  private isRedirecting = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private lookupDataService: LookupDataService,
    public notificationService: NotificationService,
    private assessmentFormService: AssessmentFormService,
    private fileService: FileService,
    private navigationService: NavigationService,
    private dialog: MatDialog,
    private componentFactory: ComponentFactoryService
  ) {
    this.formTypes = this.navigationService.getFormTypes();
    this.scrollToTop();
    this.selectedForm = this.navigationService.getSelectedRoute();
  }

  


  ngOnInit() {
    
    if (this.checkForValidRouteOrRedirect()) {
      this.isRedirecting = true;
      this.destroy$.next();
      return;
    }

    this.initializeForm();

  if (!this.model) {
    console.error('Model failed to initialize!');
    return;
  }

    this.setupComponentFactorySubscriptions();

    if (this.navigationService.routeIsGeneralEnquiry()) {
      this.typeChange('visa');
    }
 
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
    const page = params['page'];
    if (page && this.viewInitialized) {
      this.handlePageChange(page);
    }
  });

  }

  private getSettingsFlags(): Observable<SettingsFlagsResult> {
    return this.assessmentFormService.getSettingsFlags().pipe(shareReplay(1),takeUntil(this.destroy$));
  }

  private loadLookupDataAndComponent(page: string): void {

  // Don't load if already loading or loaded
  if (this.componentLoading || this.currentComponent) {
    return;
  }

  
   forkJoin({
    lookupData: this.lookupDataService.loadLookupData(), 
    settingsFlags: this.getSettingsFlags().pipe(first())
  }).pipe(
    takeUntil(this.destroy$)
  ).subscribe({
    next: ({ lookupData, settingsFlags }) => {

      this.lookupData = lookupData;
      this.settingsFlags = settingsFlags.initialAssessmentSettings;
      // Only proceed if model is ready
      if (!this.model) {
        console.error('Model not ready when lookup data received');
        return;
      }
      
      if (page && !this.currentComponent) {
        this.loadComponent(page);
        this.pendingPageToLoad = undefined;
      }
    },
    error: (error) => {
      console.error('Error loading lookup data:', error);
      this.notificationService.setError('Failed to load form data');
    }
  });
}

  ngAfterViewInit() {
  if (this.isRedirecting) {
      console.log('Component is redirecting - skipping view initialization');
      return;
  }
  this.viewInitialized = true;
  
  setTimeout(() => {
    const currentPage = this.getCurrentPage();
    
    if (currentPage && !this.currentComponent) { 
      this.pendingPageToLoad = currentPage;
      this.loadLookupDataAndComponent(currentPage);
    } else {
      console.error('No current page found in route!');
    }
  }, 0); 
  

  }

  private handlePageChange(page: string): void {
 if (!page) return;
 
      this.currentComponent = null; 

    if (this.viewInitialized ) {
       this.loadLookupDataAndComponent(page);
    } else {
      this.pendingPageToLoad = page;
      
    }
}

private setupComponentFactorySubscriptions(): void {
    this.componentFactory.componentLoading$.pipe(takeUntil(this.destroy$)).subscribe(
      loading => this.componentLoading = loading
    );

    this.componentFactory.componentReady$.pipe(takeUntil(this.destroy$)).subscribe(
      component => this.currentComponent = component
    );
  }

  private loadComponent(page: string): void {

    
    if (!page) {
      console.error('loadComponent called with empty page!');
      return;
    }
    
    if (!this.dynamicComponent) {
      console.error('dynamicComponent ViewContainerRef is not available!');
      return;
    }
    
    const data = this.buildComponentData();

    
    const outputHandlers = {
      onChildFormReady: (form: FormGroup) => this.onChildFormReady(form)
    };

    this.componentFactory.loadComponent(page, this.dynamicComponent, data, outputHandlers);

  }
  
private buildComponentData(): ComponentFactoryData {
  
  if (!this.model) {
    console.error('Model is not initialized!');
  }


    return {
      model: this.model,
      formTypes: this.formTypes,
      selectedForm: this.selectedForm,
      enquiryType: this.enquiryType,
      formSubmitted: this.formSubmitted,
      isAddressForced: this.isAddressForced,
      lookupData: this.lookupData
    };
  }

 get isComponentReady(): boolean {
  return !this.componentLoading && !!this.currentComponent;
}


  ngOnDestroy() {
    this.componentFactory.destroyCurrentComponent();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private scrollToTop() {
    // Angular equivalent of $location.hash('nav-top'); $anchorScroll();
    setTimeout(() => {
      document.getElementById('nav-top')?.scrollIntoView();
    }, 0);
  }

  private initializeForm() {


    this.wasLoaded = this.assessmentFormService.hasFormAndLoadedFormId();

    const form = this.assessmentFormService.getForm();
    if (!form) {
      throw new Error('No application form set');
    }

    this.model = form;
  }



  private checkForValidRouteOrRedirect(): boolean {
   if (!this.assessmentFormService.getForm()) {
      if (!this.navigationService.routeIsGeneralEnquiry()) {
        this.isRedirecting = true;
        this.navigationService.redirectToHome();
        return true;
      }

      const formType = this.navigationService.getFormTypes().generic;
      this.navigationService.setSelectedRoute(formType);
      this.assessmentFormService.createForm(formType);
    }

    this.selectedForm = this.navigationService.selectedRoute;
    return false;
  }


getCurrentPage(): string {
  const page = this.activatedRoute.snapshot.params['page'];
  return page || '';
}

  getCurrentPageIndex(): number {
    const currentPage = this.getCurrentPage();
    return this.navigationService.getPageIndex(currentPage);
  }


  getCurrentPageComponent(): IFormComponent | null {
    return this.currentComponent;
  }



  initSettingsFlags(): Observable<SettingsFlagsResult> {
  
      return this.assessmentFormService.getSettingsFlags()
      .pipe(
          shareReplay(1)
      );
        

  }

  public get isAddressForced() { 
    return !!(this.settingsFlags & SettingsFlags.isAddressForced);
  }

  ageDifference = (value: Date | string): number | null => {
    const now = new Date();
    let dob: Date;

    // Handle different input types
    if (value instanceof Date) {
      dob = value;
    } else if (typeof value === 'string') {
      // Try to parse DD-MM-YYYY format or ISO string
      if (value.includes('-') && value.length === 10) {
        const parts = value.split('-');
        if (parts.length === 3) {
          // Assume DD-MM-YYYY format
          dob = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        } else {
          dob = new Date(value);
        }
      } else {
        dob = new Date(value);
      }
    } else {
      return null;
    }

    // Check if date is valid
    if (isNaN(dob.getTime())) {
      return null;
    }

    // Calculate age in years
    const ageInMilliseconds = now.getTime() - dob.getTime();
    const ageInYears = ageInMilliseconds / (1000 * 60 * 60 * 24 * 365.25);
    
    return Math.floor(ageInYears);
  }

  resetSponsor = () => {
    if (this.model.hasSponsor === 'true' && this.model.sponsorDetails === null) {
      this.model.sponsorDetails = new SponsorDetails();
    } else if (this.model.hasSponsor === 'false') {
      this.model.sponsorDetails = null;
    }
  }

  getNameApplicant = () => {
    if (this.model.applicantDetails) {
      return this.model.applicantDetails.firstName + ' ' + this.model.applicantDetails.surname;
    }
    return '';
  }

  getNameApplicantOwnership = () => {
    const appName = this.getNameApplicant();
    if (appName === '') return '';

    const lastChar = appName.trim().split('').pop();
    return lastChar === 's' ? appName + "'" : appName + "'s";
  }

  getNameSponsor = () => {
    return this.model.sponsorDetails && this.model.sponsorDetails.firstName.length > 0
      ? this.model.sponsorDetails.firstName + ' ' + this.model.sponsorDetails.surname
      : this.model.contactDetails.firstName + ' ' + this.model.contactDetails.surname;
  }

  getCreatedDate = () => {
    return this.assessmentFormService.getCreatedDate();
  }

  removeFile = (fileType: string) => {
    let file = {
      id: '',
      key: ''
    };

    if (fileType === 'resume' && this.model.applicantDetails) {
      file.id = this.model.applicantDetails.resumeLocation;
      file.key = this.model.applicantDetails.resumeFile[0].key;
    }
    if (fileType === 'decision' && this.model.decision) {
      file.id = this.model.decision.fileLocation;
      file.key = this.model.decision.decisionFile[0].key;
    }

    this.fileService.deleteFile(file).subscribe({
      next: (result) => {
        if (fileType === 'resume' && this.model.applicantDetails) {
          this.model.applicantDetails.resumeLocation = '';
          this.model.applicantDetails.resumeFile = [];
        }
        if (fileType === 'decision' && this.model.decision) {
          this.model.decision.fileLocation = '';
          this.model.decision.decisionFile = [];
        }
      },
      error: (error) => {
        this.notificationService.setError(error.error.Message);
      }
    });
  };

  relationshipChange = () => {
    if (this.model.relationshipPartner) {
      if (this.model.relationshipPartner.status !== 'engaged' && this.model.relationshipPartner.status !== 'interdependent') {
        this.model.relationshipPartner.metInPerson = 'true';
      } else {
        this.model.relationshipPartner.metInPerson = '';
      }
    }
  };

  typeChange = (enquiryType: string) => {
    this.enquiryType = enquiryType;
    this.assessmentFormService.updateFormForGeneralEnquiryType(this.enquiryType);
  }

  checkValidation = () => {
    const currentPageComponent = this.getCurrentPageComponent();
    if (currentPageComponent && !currentPageComponent.isFormValid()) {
      currentPageComponent.markFormAsTouched();
      return;
    }
    
    if (!currentPageComponent) {
      console.warn('No component available for validation');
      return;
    }
  }


  onOptions = () => {
   const deleteDialogRef = this.dialog.open(DeleteFormDialogComponent, {
        panelClass: 'dialog-panel-large',
        maxHeight: 'none'
        });
    
       deleteDialogRef.afterClosed().subscribe(result => {
        if (result === 'deleted') {
          this.onFormDeleted();
        }
  });
  }

  onFormDeleted = () => {
    setTimeout(() => {
      setTimeout(() => {
        this.navigationService.redirectToHome();
      }, 500);
    }, 800);
  }

  onSave = () => {
    //load the save dialog
    const saveDialogRef = this.dialog.open(SaveFormDialogComponent, {
        panelClass: 'dialog-panel-large',
        maxHeight: 'none'
        });
    
       saveDialogRef.afterClosed().subscribe(result => {
        if (result === 'saved') {
          this.showSaveSuccessAlert();
        }
        if(result === 'error') {
          this.showSaveErrorAlert('An error occurred while saving the form.');
        }
  });
}

onSubmit = () => {
  const submitDialogRef = this.dialog.open(SubmitFormDialogComponent, {
        panelClass: 'dialog-panel-large',
        maxHeight: 'none'
        });
    
       submitDialogRef.afterClosed().subscribe(result => {
            if (result === true) {
              this.navigationService.redirectToThankyou();
        }
        if(result === false) {
          this.showSaveErrorAlert('An error occurred while submitting the form.');
        }
  });
}

       

  
  showSaveSuccessAlert() {
    this.saveResultMessageType = 'success';
    this.saveResultMessage = 'Save successful!';
    setTimeout(() => {
      this.fadeOutAlert = true;
      setTimeout(() => {
        this.saveResultMessage = null;
        this.fadeOutAlert = false;
      }, 500);
    }, 2500);
  }

  showSaveErrorAlert(message: string) {
    this.saveResultMessageType = 'error';
    this.saveResultMessage = message;
     setTimeout(() => {
      this.fadeOutAlert = true;
      setTimeout(() => {
        this.saveResultMessage = null;
        this.fadeOutAlert = false;
      }, 500);
    }, 2500);
  }

  onModelChange = (updatedModel: InitialAssessmentForm) => {
    this.model = updatedModel;
    this.assessmentFormService.updateFormState(updatedModel);
  }
}